import { useState, type ReactNode } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import { formatLabel } from "../labels";
import type { CreatorApplicationDecisionType, DataClassification } from "../types";

export function ReasonApprovalDialog({
  open,
  title,
  notice,
  actions,
  extraFields,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  title: string;
  notice?: string;
  actions: string[];
  extraFields?: ReactNode;
  onClose: () => void;
  onSubmit: (input: {
    action: string;
    reason?: string;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState(actions[0] ?? "");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(approvalId.trim() ? { approval_id: approvalId.trim() } : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value)}
          options={actions.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {extraFields}
        <Textarea
          label="Reason (optional unless the API requires it)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <ApprovalPickerField
          label="Approved Approval (Dual for override/restore)"
          value={approvalId}
          onChange={setApprovalId}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

function FormDialog({
  open,
  title,
  notice,
  submitLabel,
  disabled,
  onClose,
  onSubmit,
  busy,
  error,
  children,
}: {
  open: boolean;
  title: string;
  notice?: string;
  submitLabel: string;
  disabled?: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  busy: boolean;
  error: string | null;
  children: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={disabled}
            onClick={() => void onSubmit()}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        {notice ? (
          <Text role="small" color="secondary" as="p">
            {notice}
          </Text>
        ) : null}
        {children}
        {error ? <Alert tone="danger" title="Request failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

type CreateProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: Record<string, unknown>) => Promise<void>;
  busy?: boolean;
  error?: string | null;
};

export function CreateProfileDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [userId, setUserId] = useState("");
  const [marketId, setMarketId] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create Creator Profile"
      notice="A Creator Profile extends an existing User (1:1). It is not a second identity."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!userId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          user_id: userId.trim(),
          ...(marketId.trim() ? { market_id: marketId.trim() } : {}),
        })
      }
    >
      <Input label="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
      <Input label="Market ID (optional)" value={marketId} onChange={(e) => setMarketId(e.target.value)} />
    </FormDialog>
  );
}

export function CreateApplicationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [profileId, setProfileId] = useState("");
  const [type, setType] = useState("verified_creator");
  const [reviewer, setReviewer] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create Application"
      notice="Rejection needs a reason."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!profileId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_profile_id: profileId.trim(),
          application_type: type,
          ...(reviewer.trim() ? { assigned_reviewer_user_id: reviewer.trim() } : {}),
        })
      }
    >
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
      <Select
        label="Application type"
        value={type}
        onChange={(e) => setType(e.target.value)}
        options={[
          // Partner and Ambassador come back as new tiers after launch
          // (owner, Play Polishing 18.2); not offered until then.
          "verified_creator",
          "creator_program",
        ].map((item) => ({ value: item, label: formatLabel(item) }))}
      />
      <Input
        label="Assigned reviewer User ID (optional)"
        value={reviewer}
        onChange={(e) => setReviewer(e.target.value)}
      />
    </FormDialog>
  );
}

export function DecideApplicationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: {
    decision: CreatorApplicationDecisionType;
    decision_reason?: string;
    reason?: string;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [decision, setDecision] = useState<CreatorApplicationDecisionType>("approve");
  const [decisionReason, setDecisionReason] = useState("");
  const [reason, setReason] = useState("");
  const [approvalId, setApprovalId] = useState("");
  return (
    <FormDialog
      open={open}
      title="Record application decision"
      notice="Reject requires a standardized reason."
      submitLabel="Record decision"
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          decision,
          ...(decisionReason.trim() ? { decision_reason: decisionReason.trim() } : {}),
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(approvalId.trim() ? { approval_id: approvalId.trim() } : {}),
        })
      }
    >
      <Select
        label="Decision"
        value={decision}
        onChange={(e) => setDecision(e.target.value as CreatorApplicationDecisionType)}
        options={[
          "approve",
          "approve_conditionally",
          "request_information",
          "hold",
          "reject",
          "escalate",
          "withdraw",
        ].map((item) => ({ value: item, label: formatLabel(item) }))}
      />
      <Textarea
        label="Decision reason"
        value={decisionReason}
        onChange={(e) => setDecisionReason(e.target.value)}
      />
      <Textarea label="Internal reason" value={reason} onChange={(e) => setReason(e.target.value)} />
      <ApprovalPickerField
        label="Approved Approval (when Manager/Dual required)"
        value={approvalId}
        onChange={setApprovalId}
      />
    </FormDialog>
  );
}

export function ReviewNoteDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [note, setNote] = useState("");
  const [classification, setClassification] = useState<DataClassification>("internal");
  return (
    <FormDialog
      open={open}
      title="Add reviewer note"
      submitLabel="Add note"
      busy={busy}
      error={error}
      disabled={!note.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          note_reference: note.trim(),
          classification,
        })
      }
    >
      <Textarea label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
      <Select
        label="Classification"
        value={classification}
        onChange={(e) => setClassification(e.target.value as DataClassification)}
        options={[
          "public",
          "internal",
          "confidential",
          "strictly_confidential",
          "regulated",
        ].map((item) => ({ value: item, label: formatLabel(item) }))}
      />
    </FormDialog>
  );
}

export function AttachFileDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [fileId, setFileId] = useState("");
  const [purpose, setPurpose] = useState("verification_document");
  return (
    <FormDialog
      open={open}
      title="Attach file"
      submitLabel="Attach"
      busy={busy}
      error={error}
      disabled={!fileId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          file_asset_id: fileId.trim(),
          purpose_type: purpose,
        })
      }
    >
      <Input label="File asset ID" value={fileId} onChange={(e) => setFileId(e.target.value)} />
      <Select
        label="Purpose"
        value={purpose}
        onChange={(e) => setPurpose(e.target.value)}
        options={[
          "verification_document",
          "creator_media",
          "audit_evidence",
          "policy_attachment",
        ].map((item) => ({ value: item, label: formatLabel(item) }))}
      />
    </FormDialog>
  );
}

export function CreateTierDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [profileId, setProfileId] = useState("");
  const [tier, setTier] = useState("verified_creator");
  const [reason, setReason] = useState("");
  const [source, setSource] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create tier history row"
      notice="Tier history is authoritative. current_tier on the profile is a summary. Overrides and reinstatement from suspended need Dual Approval."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!profileId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_profile_id: profileId.trim(),
          tier,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
          ...(source.trim() ? { source_application_id: source.trim() } : {}),
        })
      }
    >
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
      <Select
        label="Tier"
        value={tier}
        onChange={(e) => setTier(e.target.value)}
        options={[
          // Partner and Ambassador come back as new tiers after launch
          // (owner, Play Polishing 18.2); not offered until then.
          "verified_creator",
        ].map((item) => ({ value: item, label: formatLabel(item) }))}
      />
      <Input
        label="Source application ID (optional)"
        value={source}
        onChange={(e) => setSource(e.target.value)}
      />
      <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
    </FormDialog>
  );
}

export function CreateProgramDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("");
  const [cadence, setCadence] = useState("");
  const [marketId, setMarketId] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create Program"
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!name.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          name: name.trim(),
          ...(owner.trim() ? { owner_user_id: owner.trim() } : {}),
          ...(cadence.trim() ? { review_cadence: cadence.trim() } : {}),
          ...(marketId.trim() ? { market_id: marketId.trim() } : {}),
        })
      }
    >
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <Input label="Owner User ID (optional)" value={owner} onChange={(e) => setOwner(e.target.value)} />
      <Input label="Review cadence (optional)" value={cadence} onChange={(e) => setCadence(e.target.value)} />
      <Input label="Market ID (optional)" value={marketId} onChange={(e) => setMarketId(e.target.value)} />
    </FormDialog>
  );
}

export function CreateMembershipDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [programId, setProgramId] = useState("");
  const [profileId, setProfileId] = useState("");
  const [mode, setMode] = useState("create_pending");
  const [entry, setEntry] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create membership"
      notice="One active membership per program and profile."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!programId.trim() || !profileId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_program_id: programId.trim(),
          creator_profile_id: profileId.trim(),
          mode,
          ...(entry.trim() ? { entry_reason: entry.trim() } : {}),
        })
      }
    >
      <Input label="Program ID" value={programId} onChange={(e) => setProgramId(e.target.value)} />
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
      <Select
        label="Entry mode"
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        options={[
          { value: "invite", label: "Invite" },
          { value: "nominate", label: "Nominate" },
          { value: "create_pending", label: "Create pending" },
        ]}
      />
      <Textarea label="Entry reason" value={entry} onChange={(e) => setEntry(e.target.value)} />
    </FormDialog>
  );
}

export function CreateCampaignDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [name, setName] = useState("");
  const [programId, setProgramId] = useState("");
  const [owner, setOwner] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create Campaign"
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!name.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          name: name.trim(),
          ...(programId.trim() ? { creator_program_id: programId.trim() } : {}),
          ...(owner.trim() ? { owner_user_id: owner.trim() } : {}),
        })
      }
    >
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
      <Input label="Program ID (optional)" value={programId} onChange={(e) => setProgramId(e.target.value)} />
      <Input label="Owner User ID (optional)" value={owner} onChange={(e) => setOwner(e.target.value)} />
    </FormDialog>
  );
}

export function CreateInvitationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [campaignId, setCampaignId] = useState("");
  const [profileId, setProfileId] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create Invitation"
      notice="Invitation response is not campaign participation."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!campaignId.trim() || !profileId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_campaign_id: campaignId.trim(),
          creator_profile_id: profileId.trim(),
        })
      }
    >
      <Input label="Campaign ID" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} />
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
    </FormDialog>
  );
}

export function CreateParticipationDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [campaignId, setCampaignId] = useState("");
  const [profileId, setProfileId] = useState("");
  const [invitationId, setInvitationId] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create Participation"
      notice="Participation is independent of invitation. invitation_id is optional linkage only."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!campaignId.trim() || !profileId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_campaign_id: campaignId.trim(),
          creator_profile_id: profileId.trim(),
          ...(invitationId.trim() ? { creator_invitation_id: invitationId.trim() } : {}),
        })
      }
    >
      <Input label="Campaign ID" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} />
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
      <Input
        label="Invitation ID (optional)"
        value={invitationId}
        onChange={(e) => setInvitationId(e.target.value)}
      />
    </FormDialog>
  );
}

export function CreateBrandDealDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [profileId, setProfileId] = useState("");
  const [owner, setOwner] = useState("");
  return (
    <FormDialog
      open={open}
      title="Record Brand Deal (lean)"
      notice="Brand Deals are a lean status record, not a commercial payment workflow. Finance owns money movement."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!profileId.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_profile_id: profileId.trim(),
          ...(owner.trim() ? { commercial_owner_user_id: owner.trim() } : {}),
        })
      }
    >
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
      <Input
        label="Commercial owner User ID (optional)"
        value={owner}
        onChange={(e) => setOwner(e.target.value)}
      />
    </FormDialog>
  );
}

export function CreatePerformanceDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [profileId, setProfileId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [campaignId, setCampaignId] = useState("");
  return (
    <FormDialog
      open={open}
      title="Create performance record"
      notice="Performance is evidence for review, not an independent tier."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!profileId.trim() || !start.trim() || !end.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_profile_id: profileId.trim(),
          period_start_at: start.trim(),
          period_end_at: end.trim(),
          ...(campaignId.trim() ? { creator_campaign_id: campaignId.trim() } : {}),
        })
      }
    >
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
      <Input
        label="Period start (ISO-8601)"
        value={start}
        onChange={(e) => setStart(e.target.value)}
      />
      <Input label="Period end (ISO-8601)" value={end} onChange={(e) => setEnd(e.target.value)} />
      <Input
        label="Campaign ID (optional)"
        value={campaignId}
        onChange={(e) => setCampaignId(e.target.value)}
      />
    </FormDialog>
  );
}

export function CreatePaymentDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateProps) {
  const [profileId, setProfileId] = useState("");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [eligibility, setEligibility] = useState("");
  return (
    <FormDialog
      open={open}
      title="Record Creator Payment eligibility"
      notice="This records eligibility/outcome only. It does not execute a Finance payout or change Booking status."
      submitLabel="Create"
      busy={busy}
      error={error}
      disabled={!profileId.trim() || !amount || !currency.trim()}
      onClose={onClose}
      onSubmit={() =>
        onSubmit({
          creator_profile_id: profileId.trim(),
          amount: Number(amount),
          currency: currency.trim(),
          ...(eligibility.trim() ? { eligibility_reason: eligibility.trim() } : {}),
        })
      }
    >
      <Input
        label="Creator Profile ID"
        value={profileId}
        onChange={(e) => setProfileId(e.target.value)}
      />
      <Input label="Amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Input label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
      <Textarea
        label="Eligibility reason"
        value={eligibility}
        onChange={(e) => setEligibility(e.target.value)}
      />
    </FormDialog>
  );
}

export function LinkPayoutDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payoutId: string) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [payoutId, setPayoutId] = useState("");
  return (
    <FormDialog
      open={open}
      title="Link Finance payout"
      notice="Optional pointer only. This does not change Finance payout status."
      submitLabel="Link"
      busy={busy}
      error={error}
      disabled={!payoutId.trim()}
      onClose={onClose}
      onSubmit={() => onSubmit(payoutId.trim())}
    >
      <Input label="Payout ID" value={payoutId} onChange={(e) => setPayoutId(e.target.value)} />
    </FormDialog>
  );
}
